import React, {useCallback} from "react";
import Form from "react-bootstrap/Form";
import {MODE_CREATE, MODE_EDIT} from "../../../codes";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Editor from "../../../common/Editor";
import useMileageLogEditor from "./useMileageLogEditor";
import InputGroup from "react-bootstrap/InputGroup";
import {DatePicker} from "../../../common/DatePicker";
import {AppStaticDropdown} from "../../../common/AppStaticDropdown";
import {Checkbox} from "../../../common/Checkbox";
import {DeleteModal} from "../../../common/AppModal";
import useProject from "../../../util/useProject";
import {AccountsDropdown} from "../../../common/AccountsDropdown";
import {CategoriesDropdown} from "../../../common/CategoriesDropdown";

export const MileageLogEditor = () => {
  const {
    show,
    t,
    mode,
    submit,
    hideMileageLogEditor,
    editedMileageLog,
    updateEditedMileageLog,
    availableCars,
    availableAccounts,
    availableCategories,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteMileageLog
  } = useMileageLogEditor();

  const {runWithExpiration} = useProject();

  return <Editor
    show={show}
    title={t(mode === MODE_CREATE ? "mileage_log_editor_title_add" : "mileage_log_editor_title_edit")}
    onShow={useCallback(() => document.getElementById("mileage-log-editor-price").focus(), [])}
    onSubmit={runWithExpiration(submit)}
    onCancel={hideMileageLogEditor}
    onDelete={mode === MODE_EDIT ? runWithExpiration(showDeleteConfirmationModal) : null}
    submitLabel={t("mileage_log_editor_button_save")}
    cancelLabel={t("mileage_log_editor_button_cancel")}
  >
    <Form.Group as={Row} className="mb-2 mt-2" controlId="mileage-log-editor-account">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_account")}</Form.Label>
      <Col sm={9}>
        <AccountsDropdown
          value={editedMileageLog.account}
          onChange={updateEditedMileageLog("account")}
          accounts={availableAccounts}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-category">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_category")}</Form.Label>
      <Col sm={9}>
        <CategoriesDropdown
          categories={availableCategories}
          value={editedMileageLog.category}
          onChange={updateEditedMileageLog("category")}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-car">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_car")}</Form.Label>
      <Col sm={9}>
        <AppStaticDropdown
          value={editedMileageLog.car}
          choices={availableCars}
          choiceValue={choice => choice.id}
          choiceLabel={choice => choice.name}
          choiceDisplay={choice => <div>{choice.name}</div>}
          onChange={updateEditedMileageLog("car")}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-price">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_price")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedMileageLog.price}
            onChange={updateEditedMileageLog("price")}
            isInvalid={editedMileageLog.errors.price}
          />

          <Form.Control.Feedback type="invalid">{t(editedMileageLog.errors.price)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-quantity">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_quantity")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedMileageLog.quantity}
            onChange={updateEditedMileageLog("quantity")}
            isInvalid={editedMileageLog.errors.quantity}
          />

          <Form.Control.Feedback type="invalid">{t(editedMileageLog.errors.quantity)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-odometer">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_odometer")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedMileageLog.odometer}
            onChange={updateEditedMileageLog("odometer")}
            isInvalid={editedMileageLog.errors.odometer}
          />

          <Form.Control.Feedback type="invalid">{t(editedMileageLog.errors.odometer)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-full">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_full")}</Form.Label>
      <Col sm={9}>
        <Checkbox
          id="mileage-log-editor-full"
          checked={editedMileageLog.full}
          onChange={updateEditedMileageLog("full")}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="mileage-log-editor-value-date">
      <Form.Label column sm={3}>{t("mileage_log_editor_field_value_date")}</Form.Label>
      <Col sm={9}>
        <DatePicker value={editedMileageLog.valueDate} onChange={updateEditedMileageLog("valueDate")}/>
      </Col>
    </Form.Group>

    <DeleteModal
      show={deleteConfirmationModal}
      title={t("modal_delete_title")}
      cancelLabel={t("modal_delete_button_cancel")}
      submitLabel={t("modal_delete_button_apply")}
      onCancel={hideDeleteConfirmationModal}
      onSubmit={onDeleteMileageLog}
    >
      {t("modal_delete_body")}
    </DeleteModal>
  </Editor>;
};
